/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import {
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  CampoInformacion,
  accessAPI, tituloBase } from "../../../shared";
import SimuladorNumeracion from "./SimuladorNumeracion";
import CamposDelScript from "./CamposDelScript";

// Un script por organismo para cada cosa que se numera. Lo escribe un admin, así
// que corre aislado: sin red, sin sistema de archivos, sin variables de entorno
// y con tope de tiempo. Si falla, la plataforma numera como siempre y el alta
// sigue: numerar nunca puede ser el motivo por el que no se puede crear algo.
const AYUDA_SCRIPT =
  "Cuerpo de una función que devuelve el nombre. Vacío usa la numeración por " +
  "defecto.";

const TIPOS = {
  proceso: {
    campo: "scriptNombreProceso",
    titulo: "Nombre del proceso",
    porDefecto: "La letra del tipo de proceso y un correlativo. Por ejemplo J-412."
  },
  certificado: {
    campo: "scriptNombreCertificado",
    titulo: "Número del certificado",
    porDefecto: "CP y el nombre del proceso. Por ejemplo CP-J-412."
  }
};

/**
 * Numeración de procesos y certificados.
 *
 * Está separada de la configuración general porque acá el script se prueba
 * contra datos reales antes de guardarlo, y esa prueba necesita el texto que hay
 * en el editor, no el que está guardado.
 */
export default function Numeracion() {
  const [configuracion, setConfiguracion] = useState(null);
  const [editando, setEditando] = useState(null);
  const [borrador, setBorrador] = useState("");
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const toast = useRef(null);

  useEffect(() => {
    document.title = "Numeración";
    accessAPI(
      "GET",
      "admin/config/general",
      null,
      (respuesta) => setConfiguracion(respuesta),
      (respuesta) => {
        setConfiguracion({});
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo cargar la configuración",
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  function abrir(tipo) {
    setBorrador(configuracion?.[TIPOS[tipo].campo] ?? "");
    setFormKey((k) => k + 1);
    setEditando(tipo);
  }

  function guardar(campos) {
    setLoaderEditar(true);
    accessAPI(
      "PATCH",
      "admin/config/general",
      campos,
      (respuesta) => {
        setConfiguracion(respuesta);
        setEditando(null);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Numeración actualizada",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo guardar",
          sticky: true
        });
      }
    );
  }

  const configFormulario = editando
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: TIPOS[editando].campo,
                tipoitemformularioid: 11,
                etiqueta: "Script",
                ayuda: AYUDA_SCRIPT,
                respuesta: configuracion?.[TIPOS[editando].campo] ?? ""
              }
            ]
          }
        ],
        botones: [{ etiqueta: "Guardar", callback: guardar }]
      }
    : null;

  function tarjeta(tipo) {
    const { campo, titulo, porDefecto } = TIPOS[tipo];
    const script = configuracion?.[campo];

    return (
      <Card key={tipo} css={{ marginTop: "1em" }}>
        <Titulo
          botones={
            <Boton icono negro callback={() => abrir(tipo)}>
              <i className="pi pi-pencil" />
            </Boton>
          }
        >
          {titulo}
        </Titulo>
        <div className="grid">
          <CampoInformacion nombreCampo="Script" anchoCompleto>
            {script ? (
              <span css={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                {script}
              </span>
            ) : (
              `Por defecto. ${porDefecto}`
            )}
          </CampoInformacion>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={!!editando}
        onHide={() => setEditando(null)}
        position="right"
        className="w-full lg:w-8"
      >
        <Titulo>{editando ? TIPOS[editando].titulo : ""}</Titulo>
        {configFormulario && (
          <>
            <CamposDelScript tipo={editando} />
            <Formulario
              key={formKey}
              config={configFormulario}
              loader={loaderEditar}
              onCambio={(campos) =>
                setBorrador(campos[TIPOS[editando].campo] ?? "")
              }
            />
            <div css={{ marginTop: "2em" }}>
              <div css={{ fontWeight: "bold", marginBottom: "0.5em" }}>Probar</div>
              <div
                css={{
                  fontSize: "0.85em",
                  color: "var(--grisTextos)",
                  marginBottom: "0.75em"
                }}
              >
                Corre el script contra un caso real sin guardarlo y sin consumir
                numeración.
              </div>
              <SimuladorNumeracion tipo={editando} codigo={borrador} />
            </div>
          </>
        )}
      </Sidebar>

      <Titulo urlVolver="/config">Numeración</Titulo>

      {!configuracion && (
        <Card>
          <Skeleton width="100%" height="6rem" />
        </Card>
      )}

      {configuracion && Object.keys(TIPOS).map(tarjeta)}
    </>
  );
}
